import { Fragment, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { useServerSdk } from "../../RootNavigator/services/ServerSdk";
import WithHelp from "../../RootNavigator/assistance/Help/WithHelp";
import { useMe } from "../../RootNavigator/services/WhoAmI";
import { useAssistance } from "../../RootNavigator/services/Assistance";
import PaymentWarnings, { getPaymentWarnings } from "./PaymentWarnings";
import PaymentTable from "./PaymentTable";
import PaymentReceipts from "./PaymentReceipts";
import PaymentValidation from "./PaymentValidation";
import PaymentSettings from "./PaymentSettings";
import PaymentReportDisplay from "./PaymentReportDisplay";
import { InstructorPaymentDetails } from "@hpo/client/models/InstructorPayment";
import { conventionPaymentRoute } from "@hpo/client/utilities/routes";
import useRouteParam from "@hpo/client/utilities/Routing/useRouteParam";
import { getPaymentLabel } from "@hpo/client/models/BeneficiaryPayment";
import Spacer from "@hpo/client/utilities/Spacer";
import Intersperse from "@hpo/client/components/Intersperse";
import Page from "@hpo/client/utilities/Page";
import PageHeader from "@hpo/client/utilities/PageHeader";
import InstructorPaymentStatusBadge from "@hpo/client/utilities/InstructorPaymentStatusBadge";
import Theme from "@hpo/client/utilities/Theme/client";
import T from "@hpo/client/components/text/Text";
import { ConventionDetails } from "@hpo/client/models/Convention";
import Tabs, { Tab } from "@hpo/client/utilities/Tabs";
import InfoBox from "@hpo/client/utilities/InfoBox";
import Role from "@hpo/client/utilities/enums/Role";

export default function ConventionPaymentScreen() {
  const server = useServerSdk();

  const conventionId = useRouteParam(conventionPaymentRoute, "conventionId");
  const paymentId = useRouteParam(conventionPaymentRoute, "paymentId");

  const onRefresh = useCallback(async () => {
    await Promise.all([
      server.suspender.refresh(server.getPayment(paymentId)),
      server.suspender.refresh(server.getConvention_(conventionId)),
    ]);
  }, [server, paymentId, conventionId]);

  const payment = server.suspender.use(server.getPayment(paymentId));
  const convention = server.suspender.use(server.getConvention_(conventionId));

  return (
    <PaymentView
      payment={payment}
      convention={convention}
      onRefresh={onRefresh}
    />
  );
}

export type PaymentViewProps = {
  payment: InstructorPaymentDetails;
  convention: ConventionDetails;
  onRefresh: () => unknown;
};

function PaymentView(props: PaymentViewProps) {
  const { payment, convention } = props;
  const me = useMe();

  const warnings = useMemo(() => getPaymentWarnings(payment), [payment]);

  const assistance = useAssistance();
  assistance.useChatWith(convention.organizations);

  return (
    <Page>
      <WithHelp
        inactive={warnings.length === 0}
        title="Points de vigilance"
        text={<PaymentWarnings payment={payment} />}
        color={Theme.warning}
      />
      <PageHeader
        title={getPaymentLabel(payment)}
        right={<InstructorPaymentStatusBadge status={payment.status} />}
        subtitle={
          payment.committeeDate
            ? `Comité technique de suivi ${dayjs(payment.committeeDate).fromNow()} (${dayjs(payment.committeeDate).format("LL")})`
            : "Date du comité technique inconnue"
        }
      />
      {payment.cancelReason ? (
        <Fragment>
          <Spacer />
          <InfoBox title="Instruction" style="warn">
            <T>
              L'instruction a été refusée pour la raison suivante :<br />
              {payment.cancelReason}
            </T>
          </InfoBox>
        </Fragment>
      ) : null}
      <Intersperse between={() => <Spacer />} append prepend>
        <PaymentReceipts {...props} />
        <Fragment>
          <T style="subtitle">Montants</T>
          <PaymentTable payment={payment} />
        </Fragment>
        <Fragment>
          <T style="subtitle">Rapport d'instruction</T>
          <PaymentReportDisplay {...props} />
        </Fragment>
        <Fragment>
          <T style="subtitle">Signature</T>
          <Spacer />
          <Tabs>
            {me.roles.includes(Role.Instructor) ? (
              <Tab id="instructor" title="Par l'instructeur">
                <PaymentValidation
                  {...props}
                  type={Role.Instructor}
                  amountProp={
                    payment.status === "under-instruction"
                      ? "requestedAmount"
                      : "instructedAmount"
                  }
                  notesProps="instructionNotes"
                  transferConditions="sous réserve de validation par le Département"
                  help={`Pour chacun des projets, saisissez le montant que vous souhaitez instruire. Les champs sont préremplis avec le montant demandé par le bénéficiaire. Vous pouvez baisser ou augmenter ce montant (dans la limite du montant disponible). Vous pouvez ajouter un commentaire en appuyant sur le bouton "loupe".`}
                  locked={payment.status !== "under-instruction"}
                  canCancel={false}
                />
              </Tab>
            ) : null}
            {me.roles.includes(Role.Level1) ? (
              <Tab id="controller" title="Validation niveau 1">
                <PaymentValidation
                  {...props}
                  type={Role.Level1}
                  amountProp={
                    payment.status === "under-level-1-validation"
                      ? "instructedAmount"
                      : "validatedAmount"
                  }
                  notesProps="validationNotes"
                  transferConditions="sous réserve de validation par le niveau 2"
                  help={`Pour chacun des projets, saisissez le montant que vous souhaitez valider. Les champs sont préremplis avec les montants suggérés par l\'instructeur. Vous pouvez baisser ou augmenter ce montant (dans la limite du montant disponible). Vous pouvez ajouter un commentaire en appuyant sur le bouton "loupe".`}
                  locked={payment.status !== "under-level-1-validation"}
                  canCancel
                />
              </Tab>
            ) : null}
            {me.roles.includes(Role.Level2) ? (
              <Tab id="financier" title="Validation niveau 2">
                <PaymentValidation
                  {...props}
                  type={Role.Level2}
                  amountProp={
                    payment.status === "under-level-2-validation"
                      ? "validatedAmount"
                      : "certifiedAmount"
                  }
                  notesProps="certificationNotes"
                  help={`Pour chacun des projets, saisissez le montant que vous souhaitez valider. Les champs sont préremplis avec les montants validés par le Département (niveau 1). Vous pouvez baisser ou augmenter ce montant (dans la limite du montant disponible). Vous pouvez ajouter un commentaire en appuyant sur le bouton "loupe".`}
                  locked={payment.status !== "under-level-2-validation"}
                  canCancel
                />
              </Tab>
            ) : null}
          </Tabs>
        </Fragment>

        {me.roles.includes(Role.Instructor) ? (
          <PaymentSettings {...props} />
        ) : null}
      </Intersperse>
    </Page>
  );
}
